import React from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ResponsiveContainer,
  ResponsiveContent,
  ResponsiveFooter,
  ResponsiveHeader,
} from '../ui/ResponsiveLayout';
import { Card, Button } from '../ui';
import { SubHeadline } from '../ui/Typography';
import { useSessionPersistence } from '../../hooks/useSessionPersistence';
import {
  CreditCardIcon,
  LightBulbIcon,
  ShieldIcon,
  SubscriptionIcon,
  CarIcon,
  MedicalIcon,
  BankIcon,
  HomeIcon,
  TaxIcon,
  DocumentsIcon,
} from '../ui/Icons';
import { FinishFlowAction } from '../../flows/FinishFlowAction';
import { documentService } from '../../services/documentService';
import type { AppDocument } from '../../types/document';

interface DebtCategory {
  key: 'credit_card' | 'personal_auto' | 'mortgage' | 'medical' | 'tax' | 'other';
  label: string;
  Icon: React.FC<{ className?: string }>;
}

interface DebtItem {
  id: string;
  category: DebtCategory['key'];
  creditor: string;
  amount?: number;
  accountNumber?: string;
  dueDate?: string;
  notes?: string;
  documents?: AppDocument[];
}

interface ExpenseCategory {
  key:
    | 'credit_card'
    | 'utilities'
    | 'insurance'
    | 'subscriptions'
    | 'transportation'
    | 'healthcare'
    | 'other';
  label: string;
  Icon: React.FC<{ className?: string }>;
}

interface ExpenseItem {
  id: string;
  category: ExpenseCategory['key'];
  name: string;
  amount?: number;
  frequency: 'monthly' | 'quarterly' | 'annually' | 'unknown';
  dueDate?: string;
  accountNumber?: string;
  autopay: boolean;
  provider?: string;
  website?: string;
  notes?: string;
  documents?: AppDocument[];
}

const normalizeDocuments = (docs: unknown): AppDocument[] => {
  return documentService.normalizeMany(docs);
};

const Frame238: React.FC = () => {
  const navigate = useNavigate();
  const { session, updateTaskProgress } = useSessionPersistence();

  const debts: DebtItem[] = (session.taskProgress?.debts?.answers?.debts ?? []).map((debt: DebtItem) => ({
    ...debt,
    documents: normalizeDocuments(debt.documents || []),
  }));

  const expenses: ExpenseItem[] = (
    session.taskProgress?.monthly_expenses?.answers?.expenses ?? []
  ).map((expense: ExpenseItem) => ({
    ...expense,
    documents: normalizeDocuments(expense.documents || []),
  }));

  const debtCategories: DebtCategory[] = [
    { key: 'credit_card', label: 'Credit Card Debt', Icon: CreditCardIcon },
    { key: 'personal_auto', label: 'Personal & Auto Loans', Icon: CarIcon },
    { key: 'mortgage', label: 'Property Mortgages', Icon: HomeIcon },
    { key: 'medical', label: 'Medical Expenses', Icon: MedicalIcon },
    { key: 'tax', label: 'Tax Debt', Icon: TaxIcon },
    { key: 'other', label: 'Other Loans', Icon: BankIcon },
  ];

  const expenseCategories: ExpenseCategory[] = [
    { key: 'credit_card', label: 'Credit Card Payments', Icon: CreditCardIcon },
    { key: 'utilities', label: 'Utility Bills', Icon: LightBulbIcon },
    { key: 'insurance', label: 'Insurance Premiums', Icon: ShieldIcon },
    { key: 'subscriptions', label: 'Recurring Subscriptions', Icon: SubscriptionIcon },
    { key: 'transportation', label: 'Transportation Expenses', Icon: CarIcon },
    { key: 'healthcare', label: 'Healthcare Expenses', Icon: MedicalIcon },
    { key: 'other', label: 'Other Expenses', Icon: BankIcon },
  ];

  const handleEditDebts = () => navigate('/frame-235');
  const handleEditExpenses = () => navigate('/frame-237');
  const handleBack = () => navigate('/frame-237');

  const totalDebtValue = debts.reduce((sum, d) => sum + (d.amount || 0), 0);
  const totalExpenseValue = expenses.reduce((sum, e) => sum + (e.amount || 0), 0);

  const formatCurrency = (val: number) =>
    new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      maximumFractionDigits: 0,
    }).format(val);

  const openDocument = (doc: AppDocument) => {
    if (!doc.id) return;
    documentService.open(doc.id);
  };

  const downloadDocument = async (doc: AppDocument) => {
    if (!doc.id) return;

    try {
      await documentService.download(doc.id, doc.name);
    } catch (error) {
      console.error('[Frame238] Failed to download document:', error);
      alert('Failed to download document.');
    }
  };

  const removeDocumentFromSection = async (
    section: 'debts' | 'expenses',
    itemId: string,
    documentIndex: number
  ) => {
    if (section === 'debts') {
      const debt = debts.find((item) => item.id === itemId);
      const docToRemove = debt?.documents?.[documentIndex];
      if (docToRemove?.id) {
        try {
          await documentService.delete(docToRemove.id);
        } catch (error) {
          console.error('[Frame238] Failed deleting debt document:', error);
          alert('Failed to delete document.');
          return;
        }
      }

      const updatedDebts = debts.map((item) => {
        if (item.id !== itemId) return item;
        return {
          ...item,
          documents: normalizeDocuments(item.documents || []).filter((_, index) => index !== documentIndex),
        };
      });

      try {
        localStorage.setItem('debts-backup', JSON.stringify(updatedDebts));
      } catch (error) {
        console.error('[Frame238] Failed saving updated debts backup:', error);
      }

      await updateTaskProgress('debts', { debts: updatedDebts }, 'in-progress');
      window.location.reload();
      return;
    }

    if (section === 'expenses') {
      const expense = expenses.find((item) => item.id === itemId);
      const docToRemove = expense?.documents?.[documentIndex];
      if (docToRemove?.id) {
        try {
          await documentService.delete(docToRemove.id);
        } catch (error) {
          console.error('[Frame238] Failed deleting expense document:', error);
          alert('Failed to delete document.');
          return;
        }
      }

      const updatedExpenses = expenses.map((item) => {
        if (item.id !== itemId) return item;
        return {
          ...item,
          documents: normalizeDocuments(item.documents || []).filter((_, index) => index !== documentIndex),
        };
      });

      try {
        localStorage.setItem('monthly-expenses-backup', JSON.stringify(updatedExpenses));
      } catch (error) {
        console.error('[Frame238] Failed saving updated expenses backup:', error);
      }

      await updateTaskProgress('monthly_expenses', { expenses: updatedExpenses }, 'in-progress');
      window.location.reload();
    }
  };

  const renderDocuments = (
    docs: AppDocument[] = [],
    section: 'debts' | 'expenses',
    itemId: string
  ) => {
    const normalizedDocs = normalizeDocuments(docs);
    if (!normalizedDocs || normalizedDocs.length === 0) return null;

    return (
      <div className="mt-2 space-y-2">
        {normalizedDocs.map((doc, index) => (
          <div
            key={`${itemId}-doc-${doc.id || index}-${index}`}
            className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 rounded-md border border-stone-200 bg-white px-3 py-2"
          >
            <button
              type="button"
              onClick={() => openDocument(doc)}
              className="flex items-start gap-2 min-w-0 w-full sm:flex-1 text-left"
            >
              <DocumentsIcon className="w-4 h-4 text-cyan-700 flex-shrink-0 mt-0.5" />
              <span
                className="text-sm font-medium text-stone-800 truncate min-w-0"
                title={doc.name}
              >
                {doc.name}
              </span>
            </button>

            <div className="flex flex-wrap items-center gap-2 sm:shrink-0">
              <button
                type="button"
                onClick={() => downloadDocument(doc)}
                className="text-cyan-600 hover:text-cyan-800 text-xs font-medium"
                title="Download document"
              >
                Download
              </button>

              <button
                type="button"
                onClick={() => removeDocumentFromSection(section, itemId, index)}
                className="text-red-500 hover:text-red-700 text-xs shrink-0"
                title="Remove document"
              >
                Remove
              </button>
            </div>
          </div>
        ))}
      </div>
    );
  };

  return (
    <ResponsiveContainer background="stone">
      <ResponsiveContent>
        <div className="space-y-6">
          <ResponsiveHeader title="Debts, Liabilities, and Expenses Summary" />

          <SubHeadline>
            Review and manage your financial obligations and recurring monthly bills.
          </SubHeadline>

          <Card>
            <h3 className="text-lg font-semibold text-stone-800 mb-4">
              Financial Obligations Overview
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="text-center border-r border-stone-100 last:border-0">
                <div className="text-2xl font-bold text-red-700">
                  {formatCurrency(totalDebtValue)}
                </div>
                <div className="text-sm text-stone-600 font-medium">Total Outstanding Debt</div>
              </div>
              <div className="text-center">
                <div className="text-2xl font-bold text-cyan-900">
                  {formatCurrency(totalExpenseValue)}
                </div>
                <div className="text-sm text-stone-600 font-medium">Total Monthly Expenses</div>
              </div>
            </div>
          </Card>

          <Card>
            <div className="flex justify-between items-start mb-4">
              <div className="flex items-center gap-2">
                <h3 className="text-lg font-semibold text-stone-800">Debts & Liabilities</h3>
                <span className="text-sm text-stone-500">({debts.length})</span>
              </div>
              <Button variant="outline" size="sm" onClick={handleEditDebts}>
                Edit
              </Button>
            </div>

            <div className="max-h-[400px] overflow-y-auto pr-2 custom-scrollbar">
              {debts.length > 0 ? (
                <div className="space-y-8">
                  {debtCategories.map((category) => {
                    const categoryDebts = debts.filter((d) => d.category === category.key);
                    if (categoryDebts.length === 0) return null;

                    return (
                      <div key={category.key}>
                        <h4 className="text-sm font-bold text-stone-400 uppercase tracking-widest mb-3 border-b border-stone-100 pb-1">
                          {category.label}
                        </h4>
                        <div className="space-y-3">
                          {categoryDebts.map((debt) => (
                            <div key={debt.id} className="bg-stone-50 rounded-lg p-4">
                              <div className="font-medium text-stone-800">{debt.creditor}</div>

                              <div className="text-sm text-stone-600 mt-1">
                                {typeof debt.amount === 'number' && 'Estimated amount provided'}
                                {debt.dueDate &&
                                  `${typeof debt.amount === 'number' ? ' • ' : ''}Due: ${new Date(
                                    debt.dueDate
                                  ).toLocaleDateString()}`}
                              </div>

                              {debt.accountNumber && (
                                <div className="text-sm text-stone-600 mt-1">
                                  Account ending in {debt.accountNumber.slice(-4)}
                                </div>
                              )}

                              {debt.notes && (
                                <div className="text-sm text-stone-600 mt-2">{debt.notes}</div>
                              )}

                              {renderDocuments(debt.documents, 'debts', debt.id)}
                            </div>
                          ))}
                        </div>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <div className="text-center py-8 text-stone-500">
                  <p>No debts have been added yet.</p>
                  <Button variant="outline" onClick={handleEditDebts} className="mt-2">
                    Add Debts
                  </Button>
                </div>
              )}
            </div>
          </Card>

          <Card>
            <div className="flex justify-between items-start mb-4">
              <div className="flex items-center gap-2">
                <h3 className="text-lg font-semibold text-stone-800">Monthly Expenses</h3>
                <span className="text-sm text-stone-500">({expenses.length})</span>
              </div>
              <Button variant="outline" size="sm" onClick={handleEditExpenses}>
                Edit
              </Button>
            </div>

            <div className="max-h-[400px] overflow-y-auto pr-2 custom-scrollbar">
              {expenses.length > 0 ? (
                <div className="space-y-8">
                  {expenseCategories.map((category) => {
                    const categoryExpenses = expenses.filter((e) => e.category === category.key);
                    if (categoryExpenses.length === 0) return null;

                    return (
                      <div key={category.key}>
                        <h4 className="text-sm font-bold text-stone-400 uppercase tracking-widest mb-3 border-b border-stone-100 pb-1">
                          {category.label}
                        </h4>
                        <div className="space-y-3">
                          {categoryExpenses.map((expense) => (
                            <div key={expense.id} className="bg-stone-50 rounded-lg p-4">
                              <div className="font-medium text-stone-800">{expense.name}</div>

                              <div className="text-sm text-stone-600 mt-1">
                                {typeof expense.amount === 'number' && 'Estimated amount provided'}
                                {`${typeof expense.amount === 'number' ? ' • ' : ''}Frequency: ${
                                  expense.frequency === 'unknown'
                                    ? 'Unknown'
                                    : expense.frequency.charAt(0).toUpperCase() +
                                      expense.frequency.slice(1)
                                }`}
                                {expense.dueDate &&
                                  ` • Due: ${new Date(expense.dueDate).toLocaleDateString()}`}
                                {expense.autopay && ' • AutoPay Enabled'}
                              </div>

                              {expense.accountNumber && (
                                <div className="text-sm text-stone-600 mt-1">
                                  Account ending in {expense.accountNumber.slice(-4)}
                                </div>
                              )}

                              {expense.provider && (
                                <div className="text-sm text-stone-600 mt-1">
                                  Provider: {expense.provider}
                                  {expense.website && (
                                    <a
                                      href={expense.website}
                                      target="_blank"
                                      rel="noopener noreferrer"
                                      className="ml-2 text-cyan-600 hover:text-cyan-700"
                                    >
                                      Visit Website →
                                    </a>
                                  )}
                                </div>
                              )}

                              {expense.notes && (
                                <div className="text-sm text-stone-600 mt-2">{expense.notes}</div>
                              )}

                              {renderDocuments(expense.documents, 'expenses', expense.id)}
                            </div>
                          ))}
                        </div>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <div className="text-center py-8 text-stone-500">
                  <p>No monthly expenses have been added yet.</p>
                  <Button variant="outline" onClick={handleEditExpenses} className="mt-2">
                    Add Expenses
                  </Button>
                </div>
              )}
            </div>
          </Card>

          <div className="mt-8 w-full max-w-md">
            <div className="text-sm text-stone-500">
              <p className="mb-2">
                💡 <strong>Note:</strong> This information helps executors identify financial
                obligations that need immediate attention.
              </p>
            </div>
          </div>
        </div>
      </ResponsiveContent>

      <ResponsiveFooter
        title="Track Debts and Final Expenses"
        subtitle="Review and manage your debts and expenses"
        progressPercent={100}
        onBack={handleBack}
        backText="Back"
        nextNode={
          <FinishFlowAction
            flowId="debts"
            onFinished={() => navigate('/frame-140')}
            buttonText="Continue to Dashboard"
            className="!bg-cyan-700 hover:!bg-cyan-800 rounded-sm"
          />
        }
      />
    </ResponsiveContainer>
  );
};

export default Frame238;